/**
 * OPFS (Origin Private File System) 管理ユーティリティ
 * 開発用のOPFS操作ヘルパー関数
 */

import logger from './logger';

/**
 * OPFSのルートディレクトリを取得
 */
async function getOPFSRoot(): Promise<FileSystemDirectoryHandle> {
  if (!navigator.storage || !navigator.storage.getDirectory) {
    throw new Error('OPFS is not supported in this browser');
  }
  return await navigator.storage.getDirectory();
}

/**
 * OPFSの全データをクリア
 * 警告: すべてのプロジェクトデータが削除されます
 */
export async function clearAllOPFSData(): Promise<void> {
  try {
    const root = await getOPFSRoot();

    // すべてのエントリを取得して削除
    for await (const entry of root.values()) {
      if (entry.kind === 'directory') {
        await root.removeEntry(entry.name, { recursive: true });
        logger.info(`[OPFSManager] Removed directory: ${entry.name}`);
      } else if (entry.kind === 'file') {
        await root.removeEntry(entry.name);
        logger.info(`[OPFSManager] Removed file: ${entry.name}`);
      }
    }

    logger.info('[OPFSManager] All OPFS data cleared');
  } catch (error) {
    logger.error('[OPFSManager] Failed to clear OPFS data:', error);
    throw error;
  }
}

/**
 * プロジェクトディレクトリの全データをクリア
 */
export async function clearProjectsDirectory(): Promise<void> {
  try {
    const root = await getOPFSRoot();

    try {
      await root.removeEntry('projects', { recursive: true });
      logger.info('[OPFSManager] Projects directory cleared');
    } catch (error) {
      if ((error as Error).name === 'NotFoundError') {
        logger.info('[OPFSManager] Projects directory does not exist');
      } else {
        throw error;
      }
    }
  } catch (error) {
    logger.error('[OPFSManager] Failed to clear projects directory:', error);
    throw error;
  }
}

/**
 * OPFSの使用状況を取得
 */
export async function getOPFSStorageInfo(): Promise<{
  projects: Array<{ name: string; size: number }>;
  totalSize: number;
}> {
  try {
    const root = await getOPFSRoot();
    const projects: Array<{ name: string; size: number }> = [];
    let totalSize = 0;

    try {
      const projectDir = await root.getDirectoryHandle('projects', { create: false });

      for await (const entry of projectDir.values()) {
        if (entry.kind === 'file') {
          const fileHandle = await projectDir.getFileHandle(entry.name);
          const file = await fileHandle.getFile();
          projects.push({
            name: entry.name,
            size: file.size
          });
          totalSize += file.size;
        }
      }
    } catch (error) {
      if ((error as Error).name !== 'NotFoundError') {
        throw error;
      }
    }

    return { projects, totalSize };
  } catch (error) {
    logger.error('[OPFSManager] Failed to get storage info:', error);
    throw error;
  }
}

/**
 * 特定のプロジェクトファイルを削除
 */
export async function deleteProjectFile(filename: string): Promise<void> {
  try {
    const root = await getOPFSRoot();
    const projectDir = await root.getDirectoryHandle('projects', { create: false });
    await projectDir.removeEntry(filename);
    logger.info(`[OPFSManager] Deleted project file: ${filename}`);
  } catch (error) {
    logger.error(`[OPFSManager] Failed to delete project file: ${filename}`, error);
    throw error;
  }
}

/**
 * IndexedDBもクリア（完全リセット用）
 */
export async function clearAllStorageData(): Promise<void> {
  try {
    // OPFSをクリア
    await clearAllOPFSData();

    // IndexedDBをクリア
    const databases = await indexedDB.databases();
    for (const db of databases) {
      if (db.name) {
        indexedDB.deleteDatabase(db.name);
        logger.info(`[OPFSManager] Deleted IndexedDB: ${db.name}`);
      }
    }

    // LocalStorageもクリア
    localStorage.clear();
    logger.info('[OPFSManager] LocalStorage cleared');

    logger.info('[OPFSManager] All storage data cleared');
  } catch (error) {
    logger.error('[OPFSManager] Failed to clear all storage data:', error);
    throw error;
  }
}

/**
 * List all files in OPFS (for debugging)
 */
async function listAllOPFSFiles(): Promise<string[]> {
  const files: string[] = [];
  try {
    const root = await getOPFSRoot();

    async function listDir(dir: FileSystemDirectoryHandle, path: string = '') {
      for await (const entry of dir.values()) {
        const fullPath = path ? `${path}/${entry.name}` : entry.name;
        if (entry.kind === 'file') {
          files.push(fullPath);
        } else if (entry.kind === 'directory') {
          const subDir = await dir.getDirectoryHandle(entry.name);
          await listDir(subDir, fullPath);
        }
      }
    }

    await listDir(root);
    return files;
  } catch (error) {
    logger.error('[OPFSManager] Failed to list OPFS files:', error);
    return [];
  }
}

// 開発用: グローバルに公開
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.opfsManager = {
    clearAllOPFSData,
    clearProjectsDirectory,
    getOPFSStorageInfo,
    deleteProjectFile,
    clearAllStorageData,
    listAllOPFSFiles
  };

  logger.info('[OPFSManager] Development utilities available in window.opfsManager');
}